import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../utils/axiosConfig';
import useFetch from '../../hooks/useFetch';
import styles from '../../styles/equipos.module.css';

const BASE_EQUIPOS = '/gestionEquipos';
const PAGE_SIZE = 50;

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

const formatFecha = (value) => {
    if (value == null || value === '') return '—';
    const str = typeof value === 'string' ? value : (value && value.toISOString?.().slice(0, 10));
    if (!str) return '—';
    const [y, m, d] = str.slice(0, 10).split('-').map(Number);
    const mes = MESES[m - 1] || '';
    return `${d} ${mes} ${y}`;
};

const EquipoMiembros = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const idEquipo = Number(id);
    const { execute } = useFetch();

    const [equipoInfo, setEquipoInfo] = useState(null);
    const [miembros, setMiembros] = useState([]);
    const [modo, setModo] = useState('completo');
    const [totalMiembros, setTotalMiembros] = useState(0);
    const [totalFilas, setTotalFilas] = useState(0);
    const [totalPaginas, setTotalPaginas] = useState(1);
    const [pagina, setPagina] = useState(1);
    const [encuestaActiva, setEncuestaActiva] = useState(null);
    const [encuestaProgramada, setEncuestaProgramada] = useState(null);
    const [encuestaUltimaFinalizada, setEncuestaUltimaFinalizada] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [feedback, setFeedback] = useState(null); // { type: 'success' | 'error', text }
    const fileInputRef = useRef(null);
    const [importing, setImporting] = useState(false);

    // Modales
    const [addModal, setAddModal] = useState(false);
    const [addForm, setAddForm] = useState({ nombre: '', apellido: '', email: '' });
    const [addError, setAddError] = useState('');
    const [removeMember, setRemoveMember] = useState(null);
    const [editMember, setEditMember] = useState(null);
    const [editForm, setEditForm] = useState({ nombre: '', apellido: '', email: '' });
    const [editError, setEditError] = useState('');
    const [importModal, setImportModal] = useState(false);
    const [editTeamModal, setEditTeamModal] = useState(false);
    const [editTeamName, setEditTeamName] = useState('');
    const [editTeamError, setEditTeamError] = useState('');
    const [deleteTeamModal, setDeleteTeamModal] = useState(false);

    const showFeedback = (type, text) => {
        setFeedback({ type, text });
        window.clearTimeout(showFeedback._t);
        showFeedback._t = window.setTimeout(() => setFeedback(null), 4000);
    };

    const fetchEquipoInfo = useCallback(async () => {
        try {
            const res = await execute(`${BASE_EQUIPOS}/equipos`, 'GET');
            const lista = Array.isArray(res?.data) ? res.data : [];
            const eq = lista.find((e) => Number(e.id_equipo) === idEquipo) || null;
            setEquipoInfo(eq);
        } catch {
            setEquipoInfo(null);
        }
    }, [execute, idEquipo]);

    const fetchMiembros = useCallback(async (opts = {}) => {
        try {
            const paginaReq = opts.pagina ?? 1;
            const busquedaReq = opts.busqueda ?? '';
            const params = new URLSearchParams({
                id_equipo: String(idEquipo),
                pagina: String(paginaReq),
                tamano_pagina: String(PAGE_SIZE),
            });
            if (String(busquedaReq).trim()) {
                params.set('busqueda', String(busquedaReq).trim());
            }
            const res = await execute(`${BASE_EQUIPOS}/usuariosEquipo?${params.toString()}`, 'GET');
            const data = res?.data;
            if (Array.isArray(data)) {
                setModo('completo');
                setMiembros(data);
                setTotalMiembros(data.length);
                setTotalFilas(data.length);
                setTotalPaginas(1);
                setEncuestaActiva(null);
                setEncuestaProgramada(null);
                setEncuestaUltimaFinalizada(null);
                return;
            }
            const lista = Array.isArray(data?.usuarios) ? data.usuarios : [];
            setModo(data?.modo === 'paginado' ? 'paginado' : 'completo');
            setMiembros(lista);
            setTotalMiembros(data?.resumen?.total ?? lista.length);
            setTotalFilas(data?.total_filas ?? lista.length);
            setTotalPaginas(data?.total_paginas ?? 1);
            if (data?.pagina) setPagina(data.pagina);
            setEncuestaActiva(data?.encuesta_activa || null);
            setEncuestaProgramada(data?.encuesta_programada || null);
            setEncuestaUltimaFinalizada(data?.encuesta_ultima_finalizada || null);
        } catch {
            setMiembros([]);
            setTotalMiembros(0);
            setTotalFilas(0);
            setTotalPaginas(1);
            setEncuestaActiva(null);
            setEncuestaProgramada(null);
            setEncuestaUltimaFinalizada(null);
        }
    }, [execute, idEquipo]);

    useEffect(() => {
        if (!idEquipo) return;
        setLoading(true);
        setPagina(1);
        setSearchTerm('');
        Promise.all([
            fetchEquipoInfo(),
            fetchMiembros({ pagina: 1, busqueda: '' }),
        ]).finally(() => setLoading(false));
    }, [idEquipo, fetchEquipoInfo, fetchMiembros]);

    useEffect(() => {
        if (!idEquipo || modo !== 'paginado') return undefined;
        const timer = window.setTimeout(() => {
            fetchMiembros({ pagina, busqueda: searchTerm });
        }, searchTerm ? 300 : 0);
        return () => window.clearTimeout(timer);
    }, [idEquipo, modo, pagina, searchTerm, fetchMiembros]);

    useEffect(() => {
        setPagina(1);
    }, [searchTerm, idEquipo]);

    // ---- Añadir miembro ----
    const openAddModal = () => {
        setAddForm({ nombre: '', apellido: '', email: '' });
        setAddError('');
        setAddModal(true);
    };

    const handleAddMember = async (e) => {
        e.preventDefault();
        setAddError('');
        const nombre = addForm.nombre.trim();
        const apellido = addForm.apellido.trim();
        const email = addForm.email.trim().toLowerCase();
        if (!nombre || !apellido || !email) {
            setAddError('Todos los campos son obligatorios.');
            return;
        }
        try {
            const res = await execute(`${BASE_EQUIPOS}/anadirUsuarioEquipo`, 'POST', {
                id_equipo: idEquipo,
                nombre,
                apellido,
                email,
            });
            const data = res?.data || {};
            if (Array.isArray(data.errores) && data.errores.length) {
                setAddError(data.errores[0]?.motivo || 'No se pudo añadir el miembro.');
                return;
            }
            if (Array.isArray(data.yaEnEquipo) && data.yaEnEquipo.length) {
                setAddError('Este usuario ya pertenece al equipo.');
                return;
            }
            setAddModal(false);
            showFeedback('success', 'Miembro añadido correctamente.');
            await fetchMiembros({ pagina, busqueda: searchTerm });
        } catch (err) {
            setAddError(err?.message || 'Error al añadir el miembro.');
        }
    };

    // ---- Quitar miembro ----
    const handleRemoveMember = async () => {
        if (!removeMember) return;
        try {
            await execute(`${BASE_EQUIPOS}/quitarUsuarioEquipo`, 'POST', {
                id_equipo: idEquipo,
                id_usuario: removeMember.id_usuario,
            });
            setRemoveMember(null);
            showFeedback('success', 'Miembro quitado del equipo.');
            await fetchMiembros({ pagina, busqueda: searchTerm });
        } catch (err) {
            setRemoveMember(null);
            showFeedback('error', err?.message || 'No se pudo quitar el miembro.');
        }
    };

    // ---- Editar miembro ----
    const openEditMember = (member) => {
        setEditMember(member);
        setEditForm({
            nombre: member.nombre || '',
            apellido: member.apellido || '',
            email: member.email || '',
        });
        setEditError('');
    };

    const handleEditMember = async (e) => {
        e.preventDefault();
        if (!editMember) return;
        setEditError('');
        const nombre = editForm.nombre.trim();
        const apellido = editForm.apellido.trim();
        const email = editForm.email.trim().toLowerCase();
        if (!nombre || !apellido || !email) {
            setEditError('Todos los campos son obligatorios.');
            return;
        }
        try {
            await execute(`${BASE_EQUIPOS}/editarUsuario`, 'PUT', {
                id_usuario: editMember.id_usuario,
                nombre,
                apellido,
                email,
            });
            setEditMember(null);
            showFeedback('success', 'Miembro actualizado correctamente.');
            await fetchMiembros({ pagina, busqueda: searchTerm });
        } catch (err) {
            setEditError(err?.message || 'Error al actualizar el miembro.');
        }
    };

    // ---- Editar / eliminar equipo ----
    const openEditTeam = () => {
        setEditTeamName(equipoInfo?.nombre_equipo || '');
        setEditTeamError('');
        setEditTeamModal(true);
    };

    const handleEditTeam = async (e) => {
        e.preventDefault();
        setEditTeamError('');
        const nombre = editTeamName.trim();
        if (!nombre) {
            setEditTeamError('El nombre del equipo es obligatorio.');
            return;
        }
        try {
            await execute(`${BASE_EQUIPOS}/editarEquipo`, 'PUT', {
                id_equipo: idEquipo,
                nombre_equipo: nombre,
            });
            setEditTeamModal(false);
            showFeedback('success', 'Equipo actualizado correctamente.');
            await fetchEquipoInfo();
        } catch (err) {
            setEditTeamError(err?.message || 'Error al actualizar el equipo.');
        }
    };

    const handleDeleteTeam = async () => {
        try {
            await execute(`${BASE_EQUIPOS}/eliminarEquipo`, 'POST', { id_equipo: idEquipo });
            navigate('/creatividades/equipos');
        } catch (err) {
            setDeleteTeamModal(false);
            showFeedback('error', err?.message || 'No se pudo eliminar el equipo.');
        }
    };

    // ---- Importar Excel ----
    const handleDownloadTemplate = async () => {
        try {
            const res = await axiosInstance.get(`${BASE_EQUIPOS}/plantillaMiembrosExcel`, {
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = 'plantilla_miembros_4c.xlsx';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            showFeedback('error', 'No se pudo descargar la plantilla.');
        }
    };

    const handleImportFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setImporting(true);
        try {
            const formData = new FormData();
            formData.append('file', file, file.name || 'importacion.xlsx');
            formData.append('id_equipo', String(idEquipo));

            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3500';
            const response = await fetch(`${apiUrl}${BASE_EQUIPOS}/importarMiembrosExcel`, {
                method: 'POST',
                body: formData,
                credentials: 'include',
            });

            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(data?.message || 'Error al importar el archivo.');
            }

            const asignados = (data.asignados || []).length;
            const creados = (data.creados || []).length;
            const errores = (data.errores || []).length;
            let msg = `Importación completada: ${asignados + creados} miembro(s) procesado(s).`;
            if (errores) msg += ` ${errores} fila(s) con errores.`;
            showFeedback('success', msg);
            setImportModal(false);
            await fetchMiembros({ pagina, busqueda: searchTerm });
        } catch (err) {
            const message = err?.message || 'Error al importar el archivo.';
            showFeedback('error', message);
        } finally {
            setImporting(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const miembrosFiltrados = miembros.filter((m) => {
        if (modo === 'paginado') return true;
        const q = searchTerm.toLowerCase();
        return (
            (m.nombre || '').toLowerCase().includes(q) ||
            (m.apellido || '').toLowerCase().includes(q) ||
            (m.email || '').toLowerCase().includes(q)
        );
    });

    const paginaActual = Math.min(pagina, totalPaginas);

    return (
        <div className={styles.container}>
            <div className={styles.membersHeader}>
                <div className={styles.membersHeaderLeft}>
                    <button
                        type="button"
                        className={styles.backIconBtn}
                        onClick={() => navigate('/creatividades/equipos')}
                        aria-label="Volver a empresas"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                    <div className={styles.membersTitleWrap}>
                        {equipoInfo?.nombre_empresa && (
                            <span className={styles.membersCompany}>{equipoInfo.nombre_empresa}</span>
                        )}
                        <h2 className={styles.membersTeamName}>{equipoInfo?.nombre_equipo || 'Equipo'}</h2>
                    </div>
                </div>
                <div className={styles.membersHeaderActions}>
                    <button type="button" className={styles.btnSecondary} onClick={openEditTeam}>Editar equipo</button>
                    <button type="button" className={styles.btnSecondaryDanger} onClick={() => setDeleteTeamModal(true)}>Eliminar equipo</button>
                </div>
            </div>

            {feedback && (
                <p className={feedback.type === 'success' ? styles.successBanner : styles.errorMsg} role="status">
                    {feedback.text}
                </p>
            )}

            <div className={styles.surveyStatusCard}>
                {encuestaActiva ? (
                    <>
                        <div className={styles.surveyStatusMain}>
                            <span className={`${styles.surveyBadge} ${styles.surveyBadgeActiva}`}>Encuesta activa</span>
                            <p className={styles.surveyStatusText}>
                                Periodo: {formatFecha(encuestaActiva.fecha_inicio)} — {formatFecha(encuestaActiva.fecha_fin)}
                            </p>
                        </div>
                        <button
                            type="button"
                            className={styles.btnSecondary}
                            onClick={() => navigate(`/creatividades/respuestas?encuestaId=${encuestaActiva.id_encuesta}`)}
                        >
                            Gestionar encuesta
                        </button>
                    </>
                ) : encuestaProgramada ? (
                    <>
                        <div className={styles.surveyStatusMain}>
                            <span className={`${styles.surveyBadge} ${styles.surveyBadgeProgramada}`}>Encuesta programada</span>
                            <p className={styles.surveyStatusText}>
                                Inicio previsto: {formatFecha(encuestaProgramada.fecha_inicio)}
                                {encuestaProgramada.fecha_inicio
                                    ? ` · Cierre: ${formatFecha(encuestaProgramada.fecha_fin)}`
                                    : ''}
                            </p>
                        </div>
                        <button
                            type="button"
                            className={styles.btnSecondary}
                            onClick={() => navigate('/creatividades/encuestas')}
                        >
                            Ir a encuestas
                        </button>
                    </>
                ) : (
                    <>
                        <div className={styles.surveyStatusMain}>
                            <span className={`${styles.surveyBadge} ${styles.surveyBadgeNeutral}`}>Sin encuesta activa</span>
                            <p className={styles.surveyStatusText}>
                                {encuestaUltimaFinalizada
                                    ? `Última encuesta finalizada (${formatFecha(encuestaUltimaFinalizada.fecha_inicio)} — ${formatFecha(encuestaUltimaFinalizada.fecha_fin)}).`
                                    : 'Este equipo no tiene ninguna encuesta en curso ni programada.'}
                            </p>
                        </div>
                        <button
                            type="button"
                            className={styles.btnSecondary}
                            onClick={() => navigate('/creatividades/encuestas')}
                        >
                            Gestionar encuestas
                        </button>
                    </>
                )}
            </div>

            <div className={styles.membersToolbar}>
                <input
                    type="text"
                    className={styles.searchInputMembers}
                    placeholder="Buscar por nombre, apellido o email…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <div className={styles.membersToolbarActions}>
                    {modo === 'paginado' && totalMiembros > 0 && (
                        <span className={styles.filterHint}>
                            {totalFilas.toLocaleString('es-ES')} de {totalMiembros.toLocaleString('es-ES')} miembros
                        </span>
                    )}
                    <button type="button" className={styles.btnSecondary} onClick={() => setImportModal(true)}>
                        Importar miembros
                    </button>
                    <button type="button" className={styles.btnPrimary} onClick={openAddModal}>
                        <span className={styles.createBtnIcon} aria-hidden>+</span>
                        Añadir miembro
                    </button>
                </div>
            </div>

            {loading ? (
                <p className={styles.loadingMsg}>Cargando miembros…</p>
            ) : (
                <div className={styles.membersTableWrap}>
                    <table className={styles.membersTable}>
                        <thead>
                            <tr>
                                <th>Nombre</th>
                                <th>Apellido</th>
                                <th>Email</th>
                                <th aria-label="Acciones"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {miembrosFiltrados.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className={styles.emptyMembers}>
                                        {miembros.length === 0
                                            ? 'Este equipo todavía no tiene miembros. Añade el primero o impórtalos desde Excel.'
                                            : 'No hay miembros que coincidan con la búsqueda.'}
                                    </td>
                                </tr>
                            ) : (
                                miembrosFiltrados.map((m) => (
                                    <tr key={m.id_usuario}>
                                        <td data-label="Nombre">{m.nombre}</td>
                                        <td data-label="Apellido">{m.apellido}</td>
                                        <td data-label="Email">{m.email}</td>
                                        <td data-label="" className={styles.memberRowActions}>
                                            <div className={styles.memberActionIcons}>
                                                <button
                                                    type="button"
                                                    className={styles.memberDeleteBtn}
                                                    onClick={() => setRemoveMember(m)}
                                                    aria-label={`Quitar a ${m.nombre} ${m.apellido} del equipo`}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" aria-hidden="true">
                                                        <path d="M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Zm400-600H280v520h400v-520ZM360-280h80v-360h-80v360Zm160 0h80v-360h-80v360ZM280-720v520-520Z" fill="currentColor" />
                                                    </svg>
                                                </button>
                                                <button
                                                    type="button"
                                                    className={styles.memberEditBtn}
                                                    onClick={() => openEditMember(m)}
                                                    aria-label={`Editar a ${m.nombre} ${m.apellido}`}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" aria-hidden="true">
                                                        <path d="M200-200h57l391-391-57-57-391 391v57Zm-80 80v-170l528-527q12-11 26.5-17t30.5-6q16 0 31 6t26 18l55 56q12 11 17.5 26t5.5 30q0 16-5.5 30.5T817-647L290-120H120Zm640-584-56-56 56 56Zm-141 85-28-29 57 57-29-28Z" fill="currentColor" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                    {modo === 'paginado' && totalPaginas > 1 && (
                        <div className={styles.membersPagination}>
                            <button
                                type="button"
                                className={styles.actionBtn}
                                onClick={() => setPagina((p) => Math.max(1, p - 1))}
                                disabled={paginaActual <= 1}
                            >
                                Anterior
                            </button>
                            <span>
                                Página <strong>{paginaActual}</strong> de {totalPaginas}
                            </span>
                            <button
                                type="button"
                                className={styles.actionBtn}
                                onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
                                disabled={paginaActual >= totalPaginas}
                            >
                                Siguiente
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Modal añadir miembro */}
            {addModal && (
                <div className={styles.modalOverlay} onClick={() => setAddModal(false)}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3>Añadir miembro</h3>
                            <button type="button" className={styles.closeBtn} onClick={() => setAddModal(false)} aria-label="Cerrar">✕</button>
                        </div>
                        <form onSubmit={handleAddMember} className={styles.form}>
                            {addError && <p className={styles.errorMsg} role="alert">{addError}</p>}
                            <div className={styles.sidebarFormRow}>
                                <div className={styles.inputGroup}>
                                    <label htmlFor="add-nombre">Nombre</label>
                                    <input id="add-nombre" type="text" value={addForm.nombre} onChange={(e) => setAddForm((f) => ({ ...f, nombre: e.target.value }))} required autoFocus />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label htmlFor="add-apellido">Apellido</label>
                                    <input id="add-apellido" type="text" value={addForm.apellido} onChange={(e) => setAddForm((f) => ({ ...f, apellido: e.target.value }))} required />
                                </div>
                            </div>
                            <div className={styles.inputGroup}>
                                <label htmlFor="add-email">Email</label>
                                <input id="add-email" type="email" value={addForm.email} onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))} required placeholder="persona@empresa.com" />
                            </div>
                            <div className={styles.formActions}>
                                <button type="button" className={styles.actionBtn} onClick={() => setAddModal(false)}>Cancelar</button>
                                <button type="submit" className={styles.submitBtn}>Añadir</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal editar miembro */}
            {editMember && (
                <div className={styles.modalOverlay} onClick={() => setEditMember(null)}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3>Editar miembro</h3>
                            <button type="button" className={styles.closeBtn} onClick={() => setEditMember(null)} aria-label="Cerrar">✕</button>
                        </div>
                        <form onSubmit={handleEditMember} className={styles.form}>
                            {editError && <p className={styles.errorMsg} role="alert">{editError}</p>}
                            <div className={styles.sidebarFormRow}>
                                <div className={styles.inputGroup}>
                                    <label htmlFor="edit-nombre">Nombre</label>
                                    <input id="edit-nombre" type="text" value={editForm.nombre} onChange={(e) => setEditForm((f) => ({ ...f, nombre: e.target.value }))} required autoFocus />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label htmlFor="edit-apellido">Apellido</label>
                                    <input id="edit-apellido" type="text" value={editForm.apellido} onChange={(e) => setEditForm((f) => ({ ...f, apellido: e.target.value }))} required />
                                </div>
                            </div>
                            <div className={styles.inputGroup}>
                                <label htmlFor="edit-email">Email</label>
                                <input id="edit-email" type="email" value={editForm.email} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} required placeholder="persona@empresa.com" />
                            </div>
                            <div className={styles.formActions}>
                                <button type="button" className={styles.actionBtn} onClick={() => setEditMember(null)}>Cancelar</button>
                                <button type="submit" className={styles.submitBtn}>Guardar cambios</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal importar miembros */}
            {importModal && (
                <div className={styles.modalOverlay} onClick={() => !importing && setImportModal(false)}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3>Importar miembros</h3>
                            <button
                                type="button"
                                className={styles.closeBtn}
                                onClick={() => !importing && setImportModal(false)}
                                aria-label="Cerrar"
                                disabled={importing}
                            >
                                ✕
                            </button>
                        </div>
                        <p className={styles.confirmText}>
                            Descarga la plantilla Excel, rellénala con los datos de los miembros y súbela para añadirlos al equipo.
                        </p>
                        <div className={styles.importBlock} style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
                            <div className={styles.importHeaderRow}>
                                <span className={styles.importLabel}>Plantilla</span>
                                <button type="button" className={styles.importTemplateLink} onClick={handleDownloadTemplate}>
                                    Descargar plantilla .xlsx
                                </button>
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                                onChange={handleImportFile}
                                style={{ display: 'none' }}
                            />
                            <button
                                type="button"
                                className={styles.importDropBtn}
                                onClick={() => !importing && fileInputRef.current?.click()}
                                disabled={importing}
                            >
                                {importing ? 'Importando…' : 'Seleccionar archivo Excel'}
                            </button>
                        </div>
                        <div className={styles.formActions}>
                            <button
                                type="button"
                                className={styles.actionBtn}
                                onClick={() => setImportModal(false)}
                                disabled={importing}
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal quitar miembro */}
            {removeMember && (
                <div className={styles.modalOverlay} onClick={() => setRemoveMember(null)}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3>Quitar miembro</h3>
                            <button type="button" className={styles.closeBtn} onClick={() => setRemoveMember(null)} aria-label="Cerrar">✕</button>
                        </div>
                        <p className={styles.confirmText}>
                            ¿Quitar a {removeMember.nombre} {removeMember.apellido} de este equipo? El usuario seguirá existiendo, solo se elimina su asignación al equipo.
                        </p>
                        <div className={styles.formActions}>
                            <button type="button" className={styles.actionBtn} onClick={() => setRemoveMember(null)}>Cancelar</button>
                            <button type="button" className={styles.dangerBtn} onClick={handleRemoveMember}>Quitar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal editar equipo */}
            {editTeamModal && (
                <div className={styles.modalOverlay} onClick={() => setEditTeamModal(false)}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3>Editar equipo</h3>
                            <button type="button" className={styles.closeBtn} onClick={() => setEditTeamModal(false)} aria-label="Cerrar">✕</button>
                        </div>
                        <form onSubmit={handleEditTeam} className={styles.form}>
                            {editTeamError && <p className={styles.errorMsg} role="alert">{editTeamError}</p>}
                            <div className={styles.inputGroup}>
                                <label htmlFor="edit-team-nombre">Nombre del equipo</label>
                                <input id="edit-team-nombre" type="text" value={editTeamName} onChange={(e) => setEditTeamName(e.target.value)} required maxLength={120} autoFocus />
                            </div>
                            <div className={styles.formActions}>
                                <button type="button" className={styles.actionBtn} onClick={() => setEditTeamModal(false)}>Cancelar</button>
                                <button type="submit" className={styles.submitBtn}>Guardar cambios</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal eliminar equipo */}
            {deleteTeamModal && (
                <div className={styles.modalOverlay} onClick={() => setDeleteTeamModal(false)}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3>Eliminar equipo</h3>
                            <button type="button" className={styles.closeBtn} onClick={() => setDeleteTeamModal(false)} aria-label="Cerrar">✕</button>
                        </div>
                        <p className={styles.confirmText}>
                            ¿Eliminar el equipo &quot;{equipoInfo?.nombre_equipo}&quot;? Esta acción no se puede deshacer.
                        </p>
                        <div className={styles.formActions}>
                            <button type="button" className={styles.actionBtn} onClick={() => setDeleteTeamModal(false)}>Cancelar</button>
                            <button type="button" className={styles.dangerBtn} onClick={handleDeleteTeam}>Eliminar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EquipoMiembros;
